// src/components/cases/CasesSidebar.js
// Complete implementation with proper toggle button hover and smooth collapsed elements

import React, { useState, useMemo, useEffect } from 'react';
import {
  Root as DropdownRoot,
  Trigger as DropdownTrigger,
  Portal as DropdownPortal,
  Content as DropdownContent,
  Item as DropdownItem,
  Arrow as DropdownArrow
} from '@radix-ui/react-dropdown-menu';
import Fuse from 'fuse.js';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  User,
  Settings,
  LogOut
} from 'lucide-react';
import { ReactComponent as BriefcasePlus } from '../../assets/icons/briefcase-plus.svg';

import CaseListItem from './CaseListItem';
import NewCaseModal from './CaseModal';
import SearchSortInput from '../ui/SearchSortInput';
import ScrollContainer from '../ui/ScrollContainer';
import IconButton from '../ui/IconButton';
import SearchEmptyState from '../ui/SearchEmptyState';
import AccountSettingsModal from '../account/AccountSettingsModal';
import BeaconDot from '../ui/BeaconDot';

/**
 * Tab Navigation Component
 */
const TabNavigation = ({
  activeTab,
  onTabChange,
  onNewCase,
  newCaseButtonRef,
  highlightButton
}) => {
  return (
    <div className="mb-app-lg">
      <div className="flex justify-between items-start">
        <div className="flex space-x-app-lg">
          <button
            onClick={() => onTabChange('active')}
            className={`group/tab text-app-sm font-normal transition-all duration-app-fast relative pt-app-xs pb-app-xs ${
              activeTab === 'active'
                ? 'text-app-gray-800'
                : 'text-app-gray-500 hover:text-app-gray-600'
            }`}
          >
            Active
            {activeTab === 'active' ? (
              <div className="absolute bottom-0 left-0 right-0 h-px bg-app-accent"></div>
            ) : (
              <div className="absolute bottom-0 left-0 right-0 h-px bg-app-gray-600 opacity-0 group-hover/tab:opacity-100 transition-opacity"></div>
            )}
          </button>
          <button
            onClick={() => onTabChange('archived')}
            className={`group/tab text-app-sm font-normal transition-all duration-app-fast relative pt-app-xs pb-app-xs ${
              activeTab === 'archived'
                ? 'text-app-gray-800'
                : 'text-app-gray-500 hover:text-app-gray-600'
            }`}
          >
            Archived
            {activeTab === 'archived' ? (
              <div className="absolute bottom-0 left-0 right-0 h-px bg-app-accent"></div>
            ) : (
              <div className="absolute bottom-0 left-0 right-0 h-px bg-app-gray-600 opacity-0 group-hover/tab:opacity-100 transition-opacity"></div>
            )}
          </button>
        </div>
        {/* Wrap the IconButton in a container for the beacon */}
        <div className="relative">
          {highlightButton && <BeaconDot className="absolute -top-1 -right-1" />}
          <IconButton
            ref={newCaseButtonRef}
            onClick={onNewCase}
            title="Create new case"
            size="lg"
            className="group/briefcase text-app-gray-600 hover:text-app-gray-700"
          >
            <BriefcasePlus/>
          </IconButton>
        </div>
      </div>
    </div>
  );
};

/**
 * Full User Dropdown Component
 */
const UserDropdown = ({ currentUser, onLogout, onSettings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = React.useRef(null);
  const [isLastItemHighlighted, setIsLastItemHighlighted] = useState(false);

  React.useEffect(() => {
    if (!isOpen || !contentRef.current) return;

    const observer = new MutationObserver(() => {
      const lastItem = contentRef.current?.querySelector('[role="menuitem"]:last-of-type');
      if (lastItem) {
        setIsLastItemHighlighted(lastItem.hasAttribute('data-highlighted'));
      }
    });

    observer.observe(contentRef.current, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-highlighted']
    });

    return () => observer.disconnect();
  }, [isOpen]);

  return (
    <DropdownRoot onOpenChange={setIsOpen}>
      <DropdownTrigger asChild>
        <button
          className="w-full flex items-center space-x-app-sm p-app-sm rounded-app-md transition-colors hover:bg-app-gray-300 data-[state=open]:bg-app-gray-300"
        >
          <div className="w-6 h-6 rounded-full flex items-center justify-center bg-app-accent border border-app-gray-900 flex-shrink-0">
            <User className="w-3 h-3 text-app-gray-900" />
          </div>
          <div className="flex-1 text-left min-w-0 overflow-hidden">
            <div className="text-app-xs font-medium text-app-gray-800 pl-app-sm whitespace-nowrap">
              {currentUser?.displayName || currentUser?.email || 'User'}
            </div>
          </div>
          <ChevronDown
            size={16}
            className={`text-app-gray-600 transition-all duration-app-normal flex-shrink-0 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
      </DropdownTrigger>

      <DropdownPortal>
        <DropdownContent
          ref={contentRef}
          className="bg-app-gray-800 text-app-white rounded-md overflow-hidden z-[100] animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
          side="top"
          align="start"
          sideOffset={8}
          style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}
        >
          <DropdownItem
            className="flex items-center space-x-app-sm px-app-sm py-app-sm text-left transition-colors hover:bg-app-gray-700 text-app-white outline-none data-[highlighted]:bg-app-gray-700 cursor-pointer group"
            onSelect={onSettings}
          >
            <Settings size={16} className="text-app-white group-hover:text-app-white group-data-[highlighted]:text-app-white" />
            <span className="text-app-sm">Account</span>
          </DropdownItem>

          <DropdownItem
            className="flex items-center space-x-app-sm px-app-sm py-app-sm text-left transition-colors hover:bg-app-gray-700 text-app-white outline-none data-[highlighted]:bg-app-gray-700 cursor-pointer group"
            onSelect={onLogout}
          >
            <LogOut size={16} className="text-app-white group-hover:text-app-white group-data-[highlighted]:text-app-white" />
            <span className="text-app-sm">Sign Out</span>
          </DropdownItem>

          {/* THE FIX: Arrow now changes color based on isLastItemHighlighted state */}
          <DropdownArrow 
            className={`z-10 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] transition-colors duration-app-fast ${
              isLastItemHighlighted 
                ? 'bg-app-gray-700 fill-app-gray-700' 
                : 'bg-app-gray-800 fill-app-gray-800'
            }`} 
          />
        </DropdownContent>
      </DropdownPortal>
    </DropdownRoot>
  );
};

/**
 * Compact User Dropdown for Collapsed Sidebar
 */
const UserDropdownCompact = ({ currentUser, onLogout, onSettings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const contentRef = React.useRef(null);
  const [isLastItemHighlighted, setIsLastItemHighlighted] = useState(false);

  React.useEffect(() => {
    if (!isOpen || !contentRef.current) return;

    const observer = new MutationObserver(() => {
      const lastItem = contentRef.current?.querySelector('[role="menuitem"]:last-of-type');
      if (lastItem) {
        setIsLastItemHighlighted(lastItem.hasAttribute('data-highlighted'));
      }
    });

    observer.observe(contentRef.current, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-highlighted']
    });

    return () => observer.disconnect();
  }, [isOpen]);

  return (
    <DropdownRoot onOpenChange={setIsOpen}>
      <DropdownTrigger asChild>
        <button
          className="flex items-center justify-center rounded-full transition-colors"
          title={currentUser?.displayName || currentUser?.email || 'User'}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center border border-app-gray-900 ${
              isHovered ? 'bg-app-accent-300' : 'bg-app-accent'
            }`}
          >
            <User className="text-app-gray-900 w-3 h-3" />
          </div>
        </button>
      </DropdownTrigger>

      <DropdownPortal>
        <DropdownContent
          ref={contentRef}
          className="bg-app-gray-800 text-app-white rounded-md overflow-hidden z-[100] min-w-[160px] animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
          side="right"
          align="end"
          sideOffset={8}
        >
          <DropdownItem
            className="flex items-center space-x-app-sm px-app-sm py-app-sm text-left transition-colors hover:bg-app-gray-700 text-app-white outline-none data-[highlighted]:bg-app-gray-700 cursor-pointer group"
            onSelect={onSettings}
          >
            <Settings size={16} className="text-app-white group-hover:text-app-white group-data-[highlighted]:text-app-white" />
            <span className="text-app-sm">Account</span>
          </DropdownItem>

          <DropdownItem
            className="flex items-center space-x-app-sm px-app-sm py-app-sm text-left transition-colors hover:bg-app-gray-700 text-app-white outline-none data-[highlighted]:bg-app-gray-700 cursor-pointer group"
            onSelect={onLogout}
          >
            <LogOut size={16} className="text-app-white group-hover:text-app-white group-data-[highlighted]:text-app-white" />
            <span className="text-app-sm">Sign Out</span>
          </DropdownItem>

          {/* THE FIX: Arrow now changes color based on isLastItemHighlighted state */}
          <DropdownArrow 
            className={`z-10 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] transition-colors duration-app-fast ${
              isLastItemHighlighted 
                ? 'bg-app-gray-700 fill-app-gray-700' 
                : 'bg-app-gray-800 fill-app-gray-800'
            }`} 
          />
        </DropdownContent>
      </DropdownPortal>
    </DropdownRoot>
  );
};

/**
 * Main Sidebar Component
 */
const CasesSidebar = ({
  cases,
  selectedCaseId,
  onSelectCase,
  onAddCase,
  onDeleteCase,
  onArchiveCase,
  isLoading,
  currentUser,
  onLogout,
  onCollapsedChange,
  forceExpanded = false,
  newCaseButtonRef,
  highlightNewCaseButton,
  documentSets = [],
  selectedSetId = null,
  onSelectSet,
  onToggleArchiveSet,
  onDeleteSet,
  onCreateDocumentSet,
  onUpdateSetName,
}) => {
  const [isNewCaseModalOpen, setIsNewCaseModalOpen] = useState(false);
  const [isAccountSettingsModalOpen, setIsAccountSettingsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('modified');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHoverAreaActive, setIsHoverAreaActive] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);

  const hasCases = cases && cases.length > 0;

  // Configure Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    const options = {
      keys: [
        { name: 'name', weight: 0.7 },
        { name: 'caseNumber', weight: 0.3 }
      ],
      threshold: 0.3,
      ignoreLocation: true,
      includeScore: true
    };
    
    return new Fuse(cases, options);
  }, [cases]);

  // Filter, search, and sort cases
  const processedCases = useMemo(() => {
    let filteredCases = cases.filter(caseItem => 
      activeTab === 'active' ? !caseItem.archived : caseItem.archived
    );

    if (searchTerm.trim()) {
      const searchResults = fuse.search(searchTerm);
      const searchedCaseIds = new Set(searchResults.map(result => result.item.id));
      filteredCases = filteredCases.filter(caseItem => searchedCaseIds.has(caseItem.id));
    }

    const sortedCases = [...filteredCases].sort((a, b) => {
      switch (sortBy) {
        case 'alphabetical':
          return a.name.localeCompare(b.name);
        case 'created':
          const aCreated = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const bCreated = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return bCreated - aCreated;
        case 'modified':
          const aModified = a.lastModified?.toDate ? a.lastModified.toDate() : new Date(a.lastModified);
          const bModified = b.lastModified?.toDate ? b.lastModified.toDate() : new Date(b.lastModified);
          return bModified - aModified;
        default:
          return 0;
      }
    });

    return sortedCases;
  }, [cases, activeTab, searchTerm, sortBy, fuse]);

  // Open the sidebar when forceExpanded becomes true (e.g. guide is active)
  useEffect(() => {
    if (forceExpanded && isCollapsed) {
      setIsCollapsed(false);
      setIsTransitioning(true);
      setTimeout(() => setIsTransitioning(false), 200);
      if (onCollapsedChange) onCollapsedChange(false);
    }
  }, [forceExpanded]);

  // Handle toggle - just flip the state
  const handleToggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    
    // Start transition - hide the button
    setIsTransitioning(true);
    setIsCollapsed(newCollapsedState);
    
    // Show button again after transition completes (200ms = duration-app-normal)
    setTimeout(() => {
      setIsTransitioning(false);
    }, 200);
    
    if (onCollapsedChange) {
      onCollapsedChange(newCollapsedState);
    }
  };

  // Handler for when delete modals open/close
  const handleModalStateChange = (isOpen) => {
    setIsAnyModalOpen(isOpen);
    // Also hide the hover area when modal opens
    if (isOpen) {
      setIsHoverAreaActive(false);
    }
  };

  const handleAddCase = (caseData) => {
    console.log('CasesSidebar - handleAddCase called with data:', caseData);
    onAddCase(caseData);
    setIsNewCaseModalOpen(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
  };

  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  return (
    <>
      {/* Hover detection wrapper - contains sidebar, extends to cover toggle button area */}
      <div
        className={`fixed left-0 top-0 h-full transition-all duration-app-normal ease-in-out z-[100] ${
          isCollapsed ? 'w-[58px]' : 'w-[234px]'
        }`}
        onMouseEnter={() => setIsHoverAreaActive(true)}
        onMouseLeave={() => setIsHoverAreaActive(false)}
      >
        {/* Sidebar container */}
        <div 
          className={`absolute left-0 top-0 h-full z-[80] bg-app-gray-200 transition-all duration-app-normal ease-in-out overflow-hidden ${
            isCollapsed ? 'w-12 bg-app-gray-400' : 'w-56'
          }`}
        >

          {/* Full sidebar content */}
          <div 
            className={`flex-1 flex flex-col min-h-0 h-full w-56 transition-all duration-app-normal ${
              isCollapsed 
                ? 'opacity-0 -translate-x-8' 
                : 'opacity-100'
            }`}
            style={{ pointerEvents: isCollapsed ? 'none' : 'auto' }}
          >
            {/* Logo */}
            <div className="px-app-xl pb-app-md pt-app-lg flex-shrink-0">
              <img 
                src="/logo.svg" 
                alt="StampStack Logo" 
                className="h-14 w-auto"
              />
            </div>
            
            {/* Tab Navigation + New Case Button */}
            <div className="px-app-xl pb-app-md flex-shrink-0">
              <TabNavigation 
                activeTab={activeTab}
                onTabChange={handleTabChange}
                onNewCase={() => setIsNewCaseModalOpen(true)}
                newCaseButtonRef={newCaseButtonRef}
                highlightButton={highlightNewCaseButton}
              />
            </div>

            {/* Search and Sort */}
            <div className="px-app-xl pb-app-md flex-shrink-0">
              <SearchSortInput 
                searchTerm={searchTerm}
                sortBy={sortBy}
                onSearchChange={handleSearchChange}
                onSortChange={handleSortChange}
              />
            </div>

            {/* Cases List - constrained scrollable area */}
            <div className="flex-1 min-h-0 px-app-xl overflow-hidden">
              <ScrollContainer
                className="h-full"
                trackMargins={{ top: 0, bottom: 0 }}
              >
                {isLoading ? (
                  <div className="py-app-xl text-center text-app-gray-800">
                    Loading cases...
                  </div>
                ) : processedCases.length === 0 ? (
                  searchTerm ? (
                    <SearchEmptyState
                      message={
                        activeTab === 'archived'
                          ? "Try a different search or check the Active tab."
                          : "Try a different search or check the Archived tab."
                      }
                      italicizedWord={activeTab === 'archived' ? 'Active' : 'Archived'}
                    />
                  ) : (
                    <div className="py-app-xl text-center text-app-gray-600 text-app-sm">
                      {activeTab === 'archived' 
                        ? "No archived cases found." 
                        : "No active cases found."}
                    </div>
                  )
                ) : (
                  <div className="space-y-[2px]">
                    {processedCases.map((caseItem) => (
                      <CaseListItem
                        key={caseItem.id}
                        caseItem={caseItem}
                        isSelected={selectedCaseId === caseItem.id}
                        onSelect={() => {
                          // If clicking the already selected case, clear document set selection
                          if (selectedCaseId === caseItem.id && selectedSetId) {
                            onSelectSet(null);
                          }
                          onSelectCase(caseItem.id);
                        }}
                        onDelete={() => onDeleteCase(caseItem.id)}
                        onArchive={() => onArchiveCase(caseItem.id)}
                        onModalStateChange={handleModalStateChange}
                        documentSets={selectedCaseId === caseItem.id ? documentSets : []}
                        selectedSetId={selectedSetId}
                        onSelectSet={onSelectSet}
                        onToggleLockSet={onToggleArchiveSet}
                        onDeleteSet={onDeleteSet}
                        onCreateDocumentSet={onCreateDocumentSet}
                        onUpdateSetName={onUpdateSetName}
                      />
                    ))}
                  </div>
                )}
              </ScrollContainer>
            </div>

            {/* User Dropdown with blur fade at top */}
            <div className="px-app-xl pb-app-lg pt-app-md flex-shrink-0 relative">
              <UserDropdown
                currentUser={currentUser}
                onLogout={onLogout}
                onSettings={() => setIsAccountSettingsModalOpen(true)}
              />
            </div>
          </div>
        </div>

        {/* Divider line - moved outside overflow-hidden container */}
        <div 
          className={`absolute top-0 bottom-0 w-[.5px] bg-app-gray-900 transition-all duration-app-normal z-[90] ${
            isCollapsed ? 'left-[48px]' : 'left-[224px]'
          }`}
        />

        {/* Toggle button - appears on hover area activation */}
        {hasCases && (
          <button
            onClick={handleToggleCollapse}
            className={`group absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-app-gray-200 border border-app-gray-900 flex items-center justify-center hover:bg-app-gray-700 z-[95] ${
              isHoverAreaActive && !isTransitioning && !isAnyModalOpen ? 'opacity-100' : 'opacity-0'
            } ${
              isCollapsed 
                ? 'left-[38px]' 
                : 'left-[214px]'
            }`}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight size={12} className="text-app-gray-900 group-hover:text-app-white" />
            ) : (
              <ChevronLeft size={12} className="text-app-gray-900 group-hover:text-app-white" />
            )}
          </button>
        )}
      </div>

      {/* Collapsed state elements */}
      {/* Logo icon at top */}
      <div 
        className={`fixed top-5 z-[105] transition-all duration-app-normal ease-in-out ${
          isCollapsed 
            ? 'left-2.5 opacity-100 delay-150' 
            : '-left-16 opacity-0'
        }`}
        style={{ pointerEvents: isCollapsed ? 'auto' : 'none' }}
      >
        <img 
          src="/logo-icon.svg"
          alt="StampStack Logo"
          className="h-7 w-7"
        />
      </div>

      {/* Compact user dropdown at bottom */}
      <div 
        className={`fixed bottom-6 z-[105] transition-all duration-app-normal ease-in-out ${
          isCollapsed 
            ? 'left-3 opacity-100 delay-150' 
            : '-left-16 opacity-0'
        }`}
        style={{ pointerEvents: isCollapsed ? 'auto' : 'none' }}
      >
        <UserDropdownCompact
          currentUser={currentUser}
          onLogout={onLogout}
          onSettings={() => setIsAccountSettingsModalOpen(true)}
        />
      </div>

      {/* New Case Modal */}
      {NewCaseModal && (
        <NewCaseModal
          isOpen={isNewCaseModalOpen}
          onClose={() => {
            console.log('CasesSidebar - Closing New Case Modal');
            setIsNewCaseModalOpen(false);
          }}
          onAddCase={handleAddCase}
        />
      )}

      {/* Account Settings Modal */}
      <AccountSettingsModal
        isOpen={isAccountSettingsModalOpen}
        onClose={() => setIsAccountSettingsModalOpen(false)}
      />
    </>
  );
};

export default CasesSidebar;